import { useState, useEffect } from "react";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatientReviewAction } from "../../../store/actions/PatientReviewAction";
import axiosInstance from "../../../services/AxiosInstance";
import completeIcon from "../../../../src/assets/images/check_11601272.png";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { handleSuccess } from "../../../services/ErrorHandlingService";

const PatientReviewList = () => {
  const dispatch = useDispatch();
  const { reviewList, loading } = useSelector((state) => state.patientReview);

  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(fetchPatientReviewAction());
  }, [dispatch]);

  // Format patient name and display names
  useEffect(() => {
    const formatted = (reviewList || []).map((item) => ({
      ...item,
      patient_name: `${item.first_name || ""} ${item.middile_name || ""} ${
        item.last_name || ""
      }`.trim(),
      display_review_by: item.review_by_name?.trim() ? item.review_by_name : "-",
      display_testimonial_by: item.testimonial_by_name?.trim() ? item.testimonial_by_name : "-",
    }));
    setDataList(formatted);
  }, [reviewList]);

  const handleApprove = (item, reviewType) => {
    // Check approval status
    const isApproved =
      reviewType === "review"
        ? item.admin_approval?.toLowerCase() === "approved"
        : item.admin_testimonial_approval?.toLowerCase() === "approved";

    if (isApproved) {
      swal("Info", `Already approved ${reviewType}`, "info");
      return;
    }

    // Check if review/testimonial exists
    const isProvided =
      reviewType === "review"
        ? item.review_provided?.toLowerCase() === "yes"
        : item.testimonial_provided?.toLowerCase() === "yes";

    if (!isProvided) {
      swal("Info", `No ${reviewType} provided to approve`, "info");
      return;
    }

    // Confirmation popup
    swal({
      title: "Approve?",
      text: `Approve ${reviewType} for ${item.patient_name}?`,
      icon: "info",
      buttons: true,
    }).then(async (yes) => {
      if (!yes) return;

      try {
        // Approval API
        const response = await axiosInstance.post("patient/adminReviewApproval", {
          patient_id: item.patient_id,
          review_id: item.review_id,
          review_type: reviewType,
        });

        // Success response check
        if (response?.data?.status === "success") {
          // Show success message
          handleSuccess(
            response?.data?.message || `${reviewType} approved successfully`
          );

          // Update local state only – NO extra API call to refetch the whole list
          setDataList((prev) =>
            prev.map((row) =>
              row.review_id === item.review_id
                ? {
                    ...row,
                    ...(reviewType === "review"
                      ? { admin_approval: "Approved" }
                      : { admin_testimonial_approval: "Approved" }),
                  }
                : row
            )
          );
        } else {
          swal("Error", response?.data?.message || "Approval failed", "error");
        }
      } catch (error) {
        console.error("Approval Error:", error);
        swal("Error", error?.response?.data?.message || "Approval failed", "error");
      }
    });
  };

  // Render cell for Review or Testimonial
  const renderCell = (item, type) => {
    const isProvided =
      type === "review"
        ? item.review_provided?.toLowerCase() === "yes"
        : item.testimonial_provided?.toLowerCase() === "yes";

    if (!isProvided) {
      return <span>-</span>;
    }

    const isApproved =
      type === "review"
        ? item.admin_approval?.toLowerCase() === "approved"
        : item.admin_testimonial_approval?.toLowerCase() === "approved";

    const canApprove = !isApproved;
    const displayName =
      type === "review" ? item.display_review_by : item.display_testimonial_by;

    return (
      <div className="d-flex align-items-center gap-2">
  <span className="whitespace-nowrap">
  {displayName}
  <span>{" - Yes"}</span>
</span>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {isApproved ? "Already approved by admin" : "Click to approve"}
            </Tooltip>
          }
        >
          <span>
            <img
              src={completeIcon}
              alt="Approve"
              style={{
                width: "20px",
                height: "20px",
                cursor: canApprove ? "pointer" : "default",
                opacity: isApproved ? 0.5 : 1,
              }}
              onClick={() => canApprove && handleApprove(item, type)}
            />
          </span>
        </OverlayTrigger>
      </div>
    );
  };

  // Search & sort
  const filteredData = dataList
    ?.filter((obj) =>
      obj.patient_name?.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      const A = a.patient_name?.toLowerCase() || "";
      const B = b.patient_name?.toLowerCase() || "";
      return sortOrder === "asc" ? A.localeCompare(B) : B.localeCompare(A);
    });

  useEffect(() => setCurrentPage(0), [searchText]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const toggleSort = () => setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) setCurrentPage(page);
  };

  return (
    <div className="card">
      <div className="card-body">
        {loading ? (
          <div className="text-center my-5">
            <Loader size="lg" />
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-striped mb-4">
                <thead className="table-light">
                  <tr>
                    <th style={{ cursor: "pointer" }} onClick={toggleSort}>
                      Patient Name {sortOrder === "asc" ? "↑" : "↓"}
                      <input
                        className="form-control form-control-sm mt-1"
                        placeholder="Search name"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th>Google Review</th>
                    <th>Testimonial</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item) => (
                    <tr key={item.review_id}>
                      <td className="align-middle">{item.patient_name || "-"}</td>
                      <td className="align-middle">{renderCell(item, "review")}</td>
                      <td className="align-middle">{renderCell(item, "testimonial")}</td>
                    </tr>
                  ))}
                  {paginatedData.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-center">
                        No reviews found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              className="pagination-wrapper"
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                flexWrap: "wrap",
                flexDirection: "column",
              }}
            >
              <div>
                Showing {filteredData.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                {Math.min((currentPage + 1) * rowsPerPage, filteredData.length)} of{" "}
                {filteredData.length} entries
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                  disabled={currentPage === 0}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === 0 ? "not-allowed" : "pointer",
                    opacity: currentPage === 0 ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>

                {(() => {
                  const pages = [];
                  const maxPagesToShow = 5;
                  const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                  const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                  if (startPage > 0) {
                    pages.push(
                      <button
                        key={0}
                        onClick={() => setCurrentPage(0)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        1
                      </button>
                    );
                    if (startPage > 1) {
                      pages.push(
                        <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                          ...
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i < endPage; i++) {
                    const isActive = currentPage === i;
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: isActive ? "#007bff" : "#fff",
                          color: isActive ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                      >
                        {i + 1}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages - 1}
                        onClick={() => setCurrentPage(totalPages - 1)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                  disabled={currentPage === totalPages - 1}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PatientReviewList;